import { Suspense, useEffect, useRef, useState } from 'react';
import { BrowserRouter as Router, Navigate } from 'react-router-dom';
import { createGlobalStyle } from 'styled-components';

import { getEnvClassicAssetData } from 'helpers/assets';
import { getEnv, setProductionEnv } from 'helpers/env';

import { getAssetMetadataCache } from 'store/assetsStore/cache';
import { useAssetsActions } from 'store/assetsStore/useAssetsStore';
import { LoginTypes } from 'store/authStore/types';
import useAuthStore from 'store/authStore/useAuthStore';

import { ModalService, StellarService } from 'services/globalServices';
import SentryService from 'services/sentry.service';
import { StellarEvents } from 'services/stellar/events/events';

import Provider from 'store';

import ErrorBoundary from 'components/ErrorBoundary';
import Footer from 'components/Footer';
import Header from 'components/Header/Header';
import ModalContainer from 'components/ModalContainer';
import TestnetBanner from 'components/TestnetBanner';
import ToastContainer from 'components/ToastContainer';

import DIceTrustlineModal from 'modals/DIceTrustlineModal';
import RemoveDownvoteTrustlineModal from 'modals/RemoveDownvoteTrustlineModal';

import AppGlobalStyle from 'styles/global-styles';
import { respondDown } from 'styles/mixins';
import { Breakpoints, COLORS } from 'styles/style-constants';

import { PageContent } from './App.styled';
import useGlobalSubscriptions from './hooks/useGlobalSubscriptions';
import AppRouter from './web/routing/AppRouter';
import RouteLoading from './web/routing/RouteLoading';
import { UrlQueryBatchProvider } from './web/routing/UrlQueryBatchProvider';

const UPDATE_ASSETS_DATE = 'update assets timestamp';
const UPDATE_PERIOD = 24 * 60 * 60 * 1000;

const App = () => {
    useGlobalSubscriptions();

    const { clearAssets } = useAssetsActions();
    const [isAssetsUpdated, setIsAssetsUpdated] = useState(false);

    const { isLogged, account, redirectURL, disableRedirect, callback, removeAuthCallback } =
        useAuthStore();

    useEffect(() => {
        if (!getEnv()) {
            setProductionEnv();
        }

        const assetUpdateTimestamp = localStorage.getItem(UPDATE_ASSETS_DATE);

        if (
            !assetUpdateTimestamp ||
            Date.now() - Number(assetUpdateTimestamp) > UPDATE_PERIOD ||
            !getAssetMetadataCache().size
        ) {
            clearAssets();
            localStorage.setItem(UPDATE_ASSETS_DATE, Date.now().toString());
            setIsAssetsUpdated(true);
        } else {
            setIsAssetsUpdated(true);
        }
    }, []);

    const reloadIfNotLoaded = () => {
        if (!isAssetsUpdated) {
            window.location.reload();
        }
    };

    useEffect(() => {
        window.addEventListener('online', reloadIfNotLoaded);

        return () => window.removeEventListener('online', reloadIfNotLoaded);
    }, [isAssetsUpdated]);

    useEffect(() => {
        const handler = (event: BeforeUnloadEvent) => {
            if (account && account.authType === LoginTypes.secret) {
                event.preventDefault();
            }
        };

        window.addEventListener('beforeunload', handler);
        return () => {
            window.removeEventListener('beforeunload', handler);
        };
    }, [account]);

    useEffect(() => {
        if (isLogged) {
            StellarService.effectsStream.start(account.accountId());
        } else {
            StellarService.effectsStream.stop();
        }
    }, [isLogged]);

    useEffect(() => {
        if (!account) {
            return;
        }
        const unsub = StellarService.event.sub(({ type }) => {
            if (type === StellarEvents.claimableUpdate) {
                const delegators = StellarService.cb.getDelegatorLocks(account.accountId());

                const { assetString: upvoteIceAssetString } = getEnvClassicAssetData('upvoteIce');
                const { assetString: governIceAssetString } = getEnvClassicAssetData('governIce');

                const neededDIceTrustline =
                    delegators.some(({ asset }) => asset === upvoteIceAssetString) &&
                    account.getAssetBalance(getEnvClassicAssetData('dIce').asset) === null;

                const neededGDIceTrustline =
                    delegators.some(({ asset }) => asset === governIceAssetString) &&
                    account.getAssetBalance(getEnvClassicAssetData('gdIce').asset) === null;

                if (neededDIceTrustline || neededGDIceTrustline) {
                    ModalService.openModal(DIceTrustlineModal, {
                        neededDIceTrustline,
                        neededGDIceTrustline,
                    });
                }
            }
        });

        return () => unsub();
    }, [account]);

    const downvoteTrustlineShown = useRef(false);

    useEffect(() => {
        if (!isLogged || !account || downvoteTrustlineShown.current) {
            return;
        }

        const downIceBalance = account.getAssetBalance(getEnvClassicAssetData('downvoteIce').asset);

        // Offer removal only for an existing, empty trustline — a non-zero balance
        // can't be removed, and `null` means there is no trustline to clean up.
        const hasEmptyDownIceTrustline = downIceBalance === 0;

        // Show on launch (already logged in) or right after login, but only when
        // no other modal is currently open.
        if (hasEmptyDownIceTrustline && !ModalService.modals.length) {
            downvoteTrustlineShown.current = true;
            ModalService.openModal(RemoveDownvoteTrustlineModal);
        }
    }, [isLogged, account]);

    useEffect(() => {
        if (isLogged) {
            SentryService.setSentryContext({
                publicKey: account.accountId(),
                authType: account.authType,
            });
        } else {
            SentryService.setSentryContext({
                publicKey: null,
                authType: null,
            });
        }
    }, [isLogged]);

    useEffect(() => {
        if (isLogged && Boolean(redirectURL)) {
            disableRedirect();
        }
    }, [isLogged, redirectURL]);

    useEffect(() => {
        if (isLogged && Boolean(callback)) {
            callback();
            removeAuthCallback();
        }
    }, [isLogged, callback]);

    useEffect(() => {
        const userAgent = window.navigator.userAgent;

        // Fix iOS functionality: tap on both sides of the dynamic island, and the phone will instantly scroll up
        if (userAgent.match(/iPad/i) || userAgent.match(/iPhone/i)) {
            document.documentElement.style.overflowX = 'unset';
            document.body.style.overflowX = 'unset';
        }
    }, []);

    return (
        <Router>
            <ErrorBoundary>
                {isLogged && Boolean(redirectURL) && <Navigate to={redirectURL} replace />}
                <TestnetBanner />
                <Header />
                <PageContent>
                    <Suspense fallback={<RouteLoading />}>
                        <UrlQueryBatchProvider>
                            {isAssetsUpdated ? <AppRouter /> : <RouteLoading />}
                        </UrlQueryBatchProvider>
                    </Suspense>
                </PageContent>
                <Footer />

                <ModalContainer />
                <ToastContainer />
            </ErrorBoundary>
        </Router>
    );
};

const BodyStyle = createGlobalStyle`
    ${respondDown(Breakpoints.md)`
        body {
            background-color: ${COLORS.gray50};
        } 
    `};
`;

const ProvidedApp = () => (
    <Provider>
        <AppGlobalStyle />
        <BodyStyle />
        <App />
    </Provider>
);
export default ProvidedApp;
